// ─── Component Bindings Registry ──────────────────────────────────────────────
//
// B-series: registration-time invariant enforcer for BindingSchemaMap.
// Validates all bindings atomically (all violations collected before throwing),
// then stores RegisteredBinding and RegisteredEventBinding entries.
//
// Invariant severity:
//   Hard Errors (throw BindingRegistrationError): B-1, B-2, B-3, B-6, B-11
//   Warnings (logger.warn, never throw):          B-4, B-5, B-7, B-8, B-9, B-10
//
// B-1:  BindingSchemaMap structure is valid (schema entry gate)
// B-2:  componentId is registered in the component registry
// B-3:  targetType is in the component's contract.bindings.allowedTargets
// B-4:  state source stateKey exists in stateMap (definitions or derived)
// B-5:  data_query source collection exists in dataMap
// B-6:  EventBinding.actionId exists in actionRegistry
// B-7:  State binding transform compiles without error
// B-8:  DataQuery binding filter expression compiles without error
// B-9:  Computed binding dependsOn keys each exist in stateMap
// B-10: EventBinding.eventName is in the component's contract.events.emits
// B-11: Computed binding expression compiles without ExpressionCompileError

import { compileExpression, ExpressionCompileError } from '@/core/expressionCompiler';
import type { BindingSchemaMap, BindingDefinition, EventBinding } from '@/core/schema/bindingSchema';
import type { StateSchemaMap } from '@/core/schema/appSchema';
import type { CollectionSchema } from '@/core/schema/appSchema';
import { requireComponent, hasComponent } from '@/components/componentRegistry';
import type { ComponentCapabilityContract } from '@/components/componentContracts';
import { actionRegistry } from '@/actions/actionRegistry';
import { logger } from '@/system/logger';

export type { ComponentCapabilityContract };

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RegisteredBinding {
  binding: BindingDefinition;
  contract: ComponentCapabilityContract;
}

export interface RegisteredEventBinding {
  event: EventBinding;
}

// ─── Errors ───────────────────────────────────────────────────────────────────

export interface BindingRegistrationViolation {
  bindingId: string;
  invariant: string;
  severity: 'error' | 'warning';
  message: string;
}

export class BindingRegistrationError extends Error {
  constructor(public readonly violations: BindingRegistrationViolation[]) {
    const errors = violations.filter(v => v.severity === 'error');
    super(
      `Binding registration failed with ${errors.length} error(s):\n` +
      errors.map(v => `  [${v.bindingId}] ${v.invariant}: ${v.message}`).join('\n')
    );
    this.name = 'BindingRegistrationError';
  }
}

// ─── Registry ─────────────────────────────────────────────────────────────────

class ComponentBindingsImpl {
  private bindings = new Map<string, RegisteredBinding>();
  private eventBindings = new Map<string, RegisteredEventBinding>();

  /**
   * Register all bindings from a BindingSchemaMap.
   * Validates atomically - all violations collected before throwing.
   * Hard errors (B-1, B-2, B-3, B-6, B-11) throw BindingRegistrationError.
   * Warnings (B-4, B-5, B-7, B-8, B-9, B-10) are emitted via logger.warn.
   */
  registerBindings(
    map: BindingSchemaMap,
    stateMap: StateSchemaMap,
    dataMap: CollectionSchema[],
  ): void {
    // ─── B-1: Schema entry gate ──────────────────────────────────────────────
    if (
      !map ||
      typeof map !== 'object' ||
      !Array.isArray(map.bindings) ||
      !Array.isArray(map.events)
    ) {
      throw new BindingRegistrationError([{
        bindingId: '<map>',
        invariant: 'B-1',
        severity: 'error',
        message: 'BindingSchemaMap must be an object with bindings[] and events[] arrays',
      }]);
    }

    const hardErrors: BindingRegistrationViolation[] = [];
    const warnings: BindingRegistrationViolation[] = [];

    // ─── Validate BindingDefinitions ─────────────────────────────────────────
    for (const binding of map.bindings) {
      const bid = binding.id ?? '<missing>';

      // B-2: component must be registered
      if (!hasComponent(binding.componentId)) {
        hardErrors.push({
          bindingId: bid,
          invariant: 'B-2',
          severity: 'error',
          message: `Component "${binding.componentId}" is not registered`,
        });
        continue; // B-3 and beyond require the component to be present
      }

      const { contract } = requireComponent(binding.componentId);

      // B-3: target type must be in contract.bindings.allowedTargets
      if (!contract.bindings.allowedTargets.includes(binding.targetType)) {
        hardErrors.push({
          bindingId: bid,
          invariant: 'B-3',
          severity: 'error',
          message: `Component "${binding.componentId}" (type "${contract.type}") does not accept binding target "${binding.targetType}". Accepted: ${contract.bindings.allowedTargets.join(', ')}`,
        });
      }

      const source = binding.source;

      if (source.type === 'state') {
        // B-4: stateKey must exist in stateMap
        const keyExistsInDefs = stateMap.definitions.some(def => {
          const scoped = def.scope === 'global'
            ? `global:${def.key}`
            : `${def.scope}:${def.scope === 'page' ? (def.pageId ?? 'unknown') : (def.componentId ?? 'unknown')}:${def.key}`;
          return scoped === source.stateKey;
        });
        const keyExistsInDerived = stateMap.derived.some(d => d.key === source.stateKey);
        if (!keyExistsInDefs && !keyExistsInDerived) {
          warnings.push({
            bindingId: bid,
            invariant: 'B-4',
            severity: 'warning',
            message: `State key "${source.stateKey}" is not defined in stateMap`,
          });
        }

        // B-7: transform expression compiles without error
        if (source.transform && source.transform.trim() !== '') {
          try {
            compileExpression(source.transform);
          } catch (e) {
            const msg = e instanceof ExpressionCompileError ? e.message : String(e);
            warnings.push({
              bindingId: bid,
              invariant: 'B-7',
              severity: 'warning',
              message: `State binding transform expression failed to compile: ${msg}`,
            });
          }
        }
      }

      if (source.type === 'data_query') {
        // B-5: collection must exist in dataMap
        if (!dataMap.some(c => c.id === source.collection)) {
          warnings.push({
            bindingId: bid,
            invariant: 'B-5',
            severity: 'warning',
            message: `Collection "${source.collection}" is not defined in dataMap`,
          });
        }

        // B-8: filter expression compiles without error
        if (source.filter && source.filter.trim() !== '') {
          try {
            compileExpression(source.filter);
          } catch (e) {
            const msg = e instanceof ExpressionCompileError ? e.message : String(e);
            warnings.push({
              bindingId: bid,
              invariant: 'B-8',
              severity: 'warning',
              message: `DataQuery filter expression failed to compile: ${msg}`,
            });
          }
        }
      }

      if (source.type === 'computed') {
        // B-11: computed expression must compile (Hard Error)
        try {
          compileExpression(source.expression);
        } catch (e) {
          const msg = e instanceof ExpressionCompileError ? e.message : String(e);
          hardErrors.push({
            bindingId: bid,
            invariant: 'B-11',
            severity: 'error',
            message: `Computed binding expression failed to compile: ${msg}`,
          });
        }

        // B-9: dependsOn keys each exist in stateMap
        for (const depKey of source.dependsOn) {
          const depInDefs = stateMap.definitions.some(def => {
            const scoped = def.scope === 'global'
              ? `global:${def.key}`
              : `${def.scope}:${def.scope === 'page' ? (def.pageId ?? 'unknown') : (def.componentId ?? 'unknown')}:${def.key}`;
            return scoped === depKey;
          });
          const depInDerived = stateMap.derived.some(d => d.key === depKey);
          if (!depInDefs && !depInDerived) {
            warnings.push({
              bindingId: bid,
              invariant: 'B-9',
              severity: 'warning',
              message: `Computed binding dependsOn key "${depKey}" is not defined in stateMap`,
            });
          }
        }
      }
    }

    // ─── Validate EventBindings ───────────────────────────────────────────────
    for (const event of map.events) {
      const eid = event.id ?? '<missing>';

      // B-2: component must be registered
      if (!hasComponent(event.componentId)) {
        hardErrors.push({
          bindingId: eid,
          invariant: 'B-2',
          severity: 'error',
          message: `Component "${event.componentId}" is not registered`,
        });
        continue;
      }

      const { contract } = requireComponent(event.componentId);

      // B-6: action must exist in action registry
      if (!actionRegistry.has(event.actionId)) {
        hardErrors.push({
          bindingId: eid,
          invariant: 'B-6',
          severity: 'error',
          message: `Action "${event.actionId}" is not registered`,
        });
      }

      // B-10: eventName should be in component's contract.events.emits
      if (
        contract.events.emits.length > 0 &&
        !contract.events.emits.includes(event.eventName)
      ) {
        warnings.push({
          bindingId: eid,
          invariant: 'B-10',
          severity: 'warning',
          message: `Event "${event.eventName}" is not declared in component "${event.componentId}" contract. Declared: ${contract.events.emits.join(', ')}`,
        });
      }
    }

    // ─── Emit warnings ───────────────────────────────────────────────────────
    for (const w of warnings) {
      logger.warn(`[componentBindings] ${w.invariant} ${w.bindingId}: ${w.message}`);
    }

    // ─── Throw on hard errors ─────────────────────────────────────────────────
    if (hardErrors.length > 0) {
      throw new BindingRegistrationError([...hardErrors, ...warnings]);
    }

    // ─── Register all ─────────────────────────────────────────────────────────
    for (const binding of map.bindings) {
      const { contract } = requireComponent(binding.componentId);
      this.bindings.set(binding.id, { binding, contract });
    }

    for (const event of map.events) {
      this.eventBindings.set(event.id, { event });
    }
  }

  clearBindingRegistry(): void {
    this.bindings.clear();
    this.eventBindings.clear();
  }

  getBinding(id: string): RegisteredBinding | undefined {
    return this.bindings.get(id);
  }

  requireBinding(id: string): RegisteredBinding {
    const entry = this.bindings.get(id);
    if (!entry) {
      throw new Error(`Binding "${id}" is not registered`);
    }
    return entry;
  }

  hasBinding(id: string): boolean {
    return this.bindings.has(id);
  }

  getAllBindings(): RegisteredBinding[] {
    return [...this.bindings.values()];
  }

  getBindingsForComponent(componentId: string): RegisteredBinding[] {
    return [...this.bindings.values()].filter(r => r.binding.componentId === componentId);
  }

  getEventBinding(id: string): RegisteredEventBinding | undefined {
    return this.eventBindings.get(id);
  }

  getAllEventBindings(): RegisteredEventBinding[] {
    return [...this.eventBindings.values()];
  }

  getEventBindingsForComponent(componentId: string): RegisteredEventBinding[] {
    return [...this.eventBindings.values()].filter(r => r.event.componentId === componentId);
  }
}

export const bindingRegistry = new ComponentBindingsImpl();

export const registerBindings = bindingRegistry.registerBindings.bind(bindingRegistry);
export const clearBindingRegistry = bindingRegistry.clearBindingRegistry.bind(bindingRegistry);
export const getBinding = bindingRegistry.getBinding.bind(bindingRegistry);
export const requireBinding = bindingRegistry.requireBinding.bind(bindingRegistry);
export const hasBinding = bindingRegistry.hasBinding.bind(bindingRegistry);
export const getAllBindings = bindingRegistry.getAllBindings.bind(bindingRegistry);
export const getBindingsForComponent = bindingRegistry.getBindingsForComponent.bind(bindingRegistry);
export const getEventBinding = bindingRegistry.getEventBinding.bind(bindingRegistry);
export const getAllEventBindings = bindingRegistry.getAllEventBindings.bind(bindingRegistry);
export const getEventBindingsForComponent = bindingRegistry.getEventBindingsForComponent.bind(bindingRegistry);
